import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const apikey = process.env.REACT_APP_API;
const GROUP_CODE = 10;
const THRESHOLD = 50000;

const fmt = (n) => {
  const num = parseFloat(n);
  if (!Number.isFinite(num)) return "0.00";
  return Math.abs(num).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const thStyle = {
  padding: "9px 12px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  whiteSpace: "nowrap",
  borderBottom: "2px solid rgba(255,255,255,0.15)",
};

const tdStyle = {
  padding: "6px 12px",
  fontSize: 12,
  borderBottom: "1px solid",
  verticalAlign: "middle",
};

// toDate is passed from ClosingStock parent
export default function TrailBalanceReport({ toDate }) {
  const companyCode = sessionStorage.getItem("Company_Code");
  const Company_Name = sessionStorage.getItem("Company_Name") || "Company";

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!toDate || !companyCode) return;
    setLoading(true);
    setError("");
    axios
      .get(`${apikey}/AgingAnalysis-Report-Debtors`, {
        params: { toDT: toDate, Company_Code: companyCode, Group_code: GROUP_CODE },
      })
      .then((res) => setReportData(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError("Failed to fetch report data."))
      .finally(() => setLoading(false));
  }, [toDate, companyCode]); // re-fetches whenever parent changes the date

  const { creditRows, debitRows, creditTotal, debitTotal } = useMemo(() => {
    const credit = [];
    const debit = [];
    reportData.forEach((item) => {
      const bal = parseFloat(item.Balance);
      if (!Number.isFinite(bal)) return;
      if (bal <= -THRESHOLD) credit.push(item);
      else if (bal >= THRESHOLD) debit.push(item);
    });
    credit.sort((a, b) => parseFloat(a.Balance) - parseFloat(b.Balance));
    debit.sort((a, b) => parseFloat(b.Balance) - parseFloat(a.Balance));
    return {
      creditRows: credit,
      debitRows: debit,
      creditTotal: credit.reduce((s, r) => s + parseFloat(r.Balance), 0),
      debitTotal: debit.reduce((s, r) => s + parseFloat(r.Balance), 0),
    };
  }, [reportData]);

  // ── Export XLSX ──────────────────────────────────────────────────────────────
  const handleExportXLSX = () => {
    const maxLen = Math.max(creditRows.length, debitRows.length);
    const wsData = [
      [Company_Name],
      [`Trial Balance — Group: ${GROUP_CODE} | As On: ${toDate}`],
      [],
      ["Ac Code (Cr)", "Ac Name (Credit Side)", "Balance (Cr)", "", "Ac Code (Dr)", "Ac Name (Debit Side)", "Balance (Dr)"],
    ];
    for (let i = 0; i < maxLen; i++) {
      const c = creditRows[i];
      const d = debitRows[i];
      wsData.push([
        c ? c.Ac_code : "", c ? c.Ac_name : "", c ? parseFloat(c.Balance) : "",
        "",
        d ? d.Ac_code : "", d ? d.Ac_name : "", d ? parseFloat(d.Balance) : "",
      ]);
    }
    wsData.push(["", "TOTAL CREDIT", creditTotal, "", "", "TOTAL DEBIT", debitTotal]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 10 }, { wch: 40 }, { wch: 18 }, { wch: 2 }, { wch: 10 }, { wch: 40 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");
    XLSX.writeFile(wb, `TrialBalance_Group${GROUP_CODE}_${toDate}.xlsx`);
  };

  // ── Print ────────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const maxLen = Math.max(creditRows.length, debitRows.length);
    const bodyRows = Array.from({ length: maxLen }, (_, i) => {
      const c = creditRows[i];
      const d = debitRows[i];
      return `<tr>
        <td style="color:#166534;font-style:italic">${c ? c.Ac_code : ""}</td>
        <td>${c ? c.Ac_name : ""}</td>
        <td class="num" style="color:#166534;font-weight:700">${c ? fmt(c.Balance) : ""}</td>
        <td class="divider"></td>
        <td style="color:#1e3a5f;font-style:italic">${d ? d.Ac_code : ""}</td>
        <td>${d ? d.Ac_name : ""}</td>
        <td class="num" style="color:#1e3a5f;font-weight:700">${d ? fmt(d.Balance) : ""}</td>
      </tr>`;
    }).join("");
    const win = window.open("", "_blank", "width=1200,height=800");
    win.document.write(`
      <html><head><title>Trial Balance</title><style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;font-size:10px;padding:16px;color:#1a1a1a}
        h2{text-align:center;font-size:15px;font-weight:800;margin-bottom:3px}
        .sub{text-align:center;font-size:10px;color:#555;margin-bottom:8px}
        .badges{display:flex;gap:16px;justify-content:center;margin-bottom:12px}
        .badge{border-radius:6px;padding:5px 14px;font-size:9px;font-weight:700}
        .cr-badge{background:#dcfce7;color:#166534;border:1px solid #86efac}
        .dr-badge{background:#dbeafe;color:#1e3a5f;border:1px solid #93c5fd}
        table{width:100%;border-collapse:collapse}
        th{padding:5px 8px;font-size:9px;text-transform:uppercase;letter-spacing:.5px;border-bottom:2px solid #ccc}
        td{padding:4px 8px;border-bottom:1px solid #e5e7eb;font-size:9.5px}
        .num{text-align:right;font-family:monospace}
        .divider{background:#1e3a5f;width:4px;padding:0;border:none}
        .cr-head{background:#166534;color:#fff;text-align:left}
        .dr-head{background:#1e3a5f;color:#fff;text-align:left}
        tfoot td{background:#1e293b;color:#fff;font-weight:700;padding:5px 8px}
        tfoot .divider{background:#0f172a}
        @page{size:A3 landscape;margin:10mm}
      </style></head><body>
        <h2>${Company_Name}</h2>
        <div class="sub">Trial Balance — Group: ${GROUP_CODE} | As On: ${toDate}</div>
        <div class="badges">
          <span class="badge cr-badge">Credit: ${creditRows.length} records | ₹ ${fmt(creditTotal)}</span>
          <span class="badge dr-badge">Debit: ${debitRows.length} records | ₹ ${fmt(debitTotal)}</span>
        </div>
        <table>
          <thead><tr>
            <th class="cr-head">Ac Code</th><th class="cr-head">Ac Name (Credit)</th><th class="cr-head" style="text-align:right">Balance</th>
            <th class="divider"></th>
            <th class="dr-head">Ac Code</th><th class="dr-head">Ac Name (Debit)</th><th class="dr-head" style="text-align:right">Balance</th>
          </tr></thead>
          <tbody>${bodyRows}</tbody>
          <tfoot><tr>
            <td colspan="2" style="text-align:right">TOTAL CREDIT</td><td class="num">₹ ${fmt(creditTotal)}</td>
            <td class="divider"></td>
            <td colspan="2" style="text-align:right">TOTAL DEBIT</td><td class="num">₹ ${fmt(debitTotal)}</td>
          </tr></tfoot>
        </table>
      </body></html>
    `);
    win.document.close();
    setTimeout(() => win.print(), 300);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: "#f8fafc", minHeight: "100%", paddingBottom: 40 }}>

      {/* ── Sub-header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", borderBottom: "1px solid #e2e8f0", background: "#fff" }}>
        {/* <div style={{ display: "flex", gap: 12 }}>
          <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, padding: "6px 16px" }}>
            <div style={{ fontSize: 9, color: "#166534", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Credit (≤ −₹50,000)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#15803d" }}>{creditRows.length} <span style={{ fontSize: 11, fontWeight: 400 }}>records</span></div>
            <div style={{ fontSize: 11, color: "#166534" }}>₹ {fmt(creditTotal)}</div>
          </div>
          <div style={{ background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 8, padding: "6px 16px" }}>
            <div style={{ fontSize: 9, color: "#1e40af", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Debit (≥ +₹50,000)</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1d4ed8" }}>{debitRows.length} <span style={{ fontSize: 11, fontWeight: 400 }}>records</span></div>
            <div style={{ fontSize: 11, color: "#1e40af" }}>₹ {fmt(debitTotal)}</div>
          </div>
        </div> */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={handleExportXLSX}
            disabled={loading || reportData.length === 0}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", opacity: (loading || reportData.length === 0) ? 0.5 : 1 }}
          >
            ⬇ Export XLSX
          </button>
          <button
            onClick={handlePrint}
            disabled={loading || reportData.length === 0}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#64748b", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", opacity: (loading || reportData.length === 0) ? 0.5 : 1 }}
          >
            🖨 Print
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "14px 24px 0" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "#64748b", fontSize: 13 }}>
            <div style={{ fontSize: 30, marginBottom: 10 }}>⏳</div>Fetching report...
          </div>
        ) : error ? (
          <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "12px 20px", fontSize: 13 }}>{error}</div>
        ) : reportData.length === 0 ? (
          <div style={{ textAlign: "center", padding: 80, color: "#94a3b8", fontSize: 13 }}>No data found for the selected date.</div>
        ) : (
          <div style={{ display: "flex", gap: 0, border: "1px solid #cbd5e1", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>

            {/* LEFT — CREDIT */}
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 520 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#166534", color: "#fff", position: "sticky", top: 0, zIndex: 2 }}>
                    <th style={{ ...thStyle, width: 85 }}>Ac Code</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>Ac Name</th>
                    <th style={{ ...thStyle, textAlign: "right", width: 130 }}>Balance (Cr)</th>
                  </tr>
                </thead>
                <tbody>
                  {creditRows.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", padding: "36px 0", color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>No credit records</td></tr>
                  ) : creditRows.map((item, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#f0fdf4" : "#fff" }}>
                      <td style={{ ...tdStyle, borderColor: "#d1fae5", color: "#166534", fontStyle: "italic", fontWeight: 700}}>{item.Ac_code}</td>
                      <td style={{ ...tdStyle, borderColor: "#d1fae5", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",textAlign: "left", }} title={item.Ac_name}>{item.Ac_name}</td>
                      <td style={{ ...tdStyle, borderColor: "#d1fae5", textAlign: "right", fontFamily: "monospace", color: "#15803d", fontWeight: 700 }}>{fmt(item.Balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#14532d", color: "#fff", fontWeight: 800 }}>
                    <td colSpan={2} style={{ ...tdStyle, borderColor: "transparent", textAlign: "right", fontSize: 11 }}>TOTAL CREDIT</td>
                    <td style={{ ...tdStyle, borderColor: "transparent", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>₹ {fmt(creditTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* DIVIDER */}
            <div style={{ width: 5, background: "linear-gradient(180deg,#1e3a5f,#3b82f6)", flexShrink: 0 }} />

            {/* RIGHT — DEBIT */}
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 520 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#1e3a5f", color: "#fff", position: "sticky", top: 0, zIndex: 2 }}>
                    <th style={{ ...thStyle, width: 85 }}>Ac Code</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>Ac Name</th>
                    <th style={{ ...thStyle, textAlign: "right", width: 130 }}>Balance (Dr)</th>
                  </tr>
                </thead>
                <tbody>
                  {debitRows.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", padding: "36px 0", color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>No debit records</td></tr>
                  ) : debitRows.map((item, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#eff6ff" : "#fff" }}>
                      <td style={{ ...tdStyle, borderColor: "#bfdbfe", color: "#1e3a5f", fontStyle: "italic", fontWeight: 700 }}>{item.Ac_code}</td>
                      <td style={{ ...tdStyle, borderColor: "#bfdbfe", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }} title={item.Ac_name}>{item.Ac_name}</td>
                      <td style={{ ...tdStyle, borderColor: "#bfdbfe", textAlign: "right", fontFamily: "monospace", color: "#1d4ed8", fontWeight: 700 }}>{fmt(item.Balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#0f2a4a", color: "#fff", fontWeight: 800 }}>
                    <td colSpan={2} style={{ ...tdStyle, borderColor: "transparent", textAlign: "right", fontSize: 11 }}>TOTAL DEBIT</td>
                    <td style={{ ...tdStyle, borderColor: "transparent", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>₹ {fmt(debitTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}